// Reference:
// * https://github.com/dev-xo/remix-saas/blob/7f415e4a76d47e22eef03a1ac9c1a52b32948ff9/app/routes/auth+/login.tsx
// * https://github.com/shadcn-ui/ui/blob/4ac9db98fe10f272dc53731e8b6ed903ae2a928c/apps/www/app/(app)/examples/authentication/page.tsx
// * https://github.com/shadcn-ui/ui/blob/79c054ac7a7b2a13acc09d72106fd40663cc9bee/apps/www/app/(app)/examples/authentication/components/user-auth-form.tsx

import { getFormProps, getInputProps, useForm } from "@conform-to/react";
import { getZodConstraint, parseWithZod } from "@conform-to/zod";
import { LoaderCircle } from "lucide-react";
import { Form, Link, type MetaFunction, useLoaderData } from "react-router";
import type { AuthorizationError } from "remix-auth";
import { HoneypotInputs } from "remix-utils/honeypot/react";
import { z } from "zod";

import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import { useIsPending } from "@/utils/form";

import { Google } from "./icons/google";

export const meta: MetaFunction = () => {
  return [{ title: `Login | ${publicEnv.siteTitle}` }];
};

const emailFormSchema = z.object({
  email: z.string().max(256).email("Invalid email address."),
});

export interface LoginLoaderResponse {
  authEmail: string;
  authError: AuthorizationError;
}

export default function Login() {
  const { authEmail, authError } = useLoaderData<LoginLoaderResponse>();
  const isEmailPending = useIsPending();
  const isGooglePending = useIsPending({ formAction: "/auth/google" });
  const isPending = isEmailPending || isGooglePending;

  const [emailForm, { email }] = useForm({
    constraint: getZodConstraint(emailFormSchema),
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: emailFormSchema });
    },
    shouldRevalidate: "onInput",
  });

  return (
    <div className="flex flex-col gap-6 text-center">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold tracking-tight">Login</h1>
        <p className="text-muted-foreground text-sm">
          If this is your first time logging in, an account will be
          automatically created.
        </p>
      </div>

      <div className="flex flex-col gap-4">
        <Form action="/auth/google" method="POST">
          <Button className="w-full" disabled={isPending} variant="outline">
            {isGooglePending && (
              <LoaderCircle className="mr-2 size-4 animate-spin" />
            )}
            <Google className="mr-2 size-5" />
            Continue with Google
          </Button>
        </Form>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background text-muted-foreground px-2">
              OR
            </span>
          </div>
        </div>

        <Form autoComplete="off" method="POST" {...getFormProps(emailForm)}>
          <HoneypotInputs />

          <div className="flex flex-col gap-2">
            <Input
              autoCapitalize="none"
              autoComplete="email"
              autoCorrect="off"
              defaultValue={authEmail || ""}
              disabled={isPending}
              placeholder="name@example.com"
              error={
                (!authError && email.errors) || (!authEmail && authError)
                  ? email.errors?.join(" ") || authError?.message
                  : ""
              }
              {...getInputProps(email, { type: "email" })}
            />

            <Button className="w-full" disabled={isPending}>
              {isEmailPending && (
                <LoaderCircle className="mr-2 size-4 animate-spin" />
              )}
              Continue with Email
            </Button>
          </div>
        </Form>
      </div>

      <p className="text-muted-foreground [&_a]:underline-decoration text-sm [&_a]:underline">
        By continuing, you agree to our
        <br />
        <Link to="/terms">Terms of Service</Link> and{" "}
        <Link to="/privacy">Privacy Policy</Link>.
      </p>
    </div>
  );
}
